import React, { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import cs from 'classnames';
import DataTable from '../../DataTable/DataTable';
import CustomTableFilters from '../../TableFilters/CustomTableFilters';
import ManufacturerModal from '../ManufacturerModal/ManufacturerModal';
import editIcon from '../../../img/edit_icon.svg';
import removeIcon from '../../../img/remove_icon.svg';
import { getManufacturers, removeManufacturer } from '../../../redux/modules/vehicles';
import { showNotification } from '../../../helpers/notificationHelpers';
import showErrorNotification from '../../../helpers/errorHandler';

import styles from '../Vehicles.module.scss';

const ManufacturersTable = () => {
  const { t } = useTranslation();
  const tableRef = useRef();
  const { manufacturers, isLoading } = useSelector((store) => store.vehiclesReducer, shallowEqual);
  const dispatch = useDispatch();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [filters, setFilters] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [manufacturer, setManufacturer] = useState({});

  const toggleFiltersBlock = () => {
    setIsCollapsed((prevState) => !prevState);
  };

  const modalHeaderText = isEmpty(manufacturer)
    ? t('vehicleModal.createManufacturer')
    : t('vehicleModal.editManufacturer');

  const loadData = useCallback(
    (params) => {
      dispatch(getManufacturers({ params, filters }));
    },
    [dispatch, filters],
  );

  const deleteManufacturer = (row) => {
    const confirmMessage = t('vehicleModal.deleteManufacturer');
    const successMessageKey = 'vehicleModal.deleteManufacturerSuccess';
    const errorMessageKey = 'REMOVE_MANUFACTURER_ERROR';

    if (window.confirm(confirmMessage)) {
      dispatch(removeManufacturer(row.id))
        .then(() => {
          showNotification(successMessageKey, 'success', errorMessageKey);
          tableRef.current.loadCurrentData();
        })
        .catch((error) => {
          showErrorNotification(error, errorMessageKey);
        });
    }
  };

  const columns = [
    {
      title: t('vehicleTbl.name'),
      property: 'name',
      sortable: true,
      filter: { type: 'string' },
    },
    {
      title: '',
      sortable: false,
      customCell: (row) => (
        <button
          type="button"
          className={styles.editIcon}
          onClick={(e) => {
            setOpenModal(true);
            setManufacturer(row);
            e.preventDefault();
          }}
          aria-label="edit vehicle"
        >
          <img alt="edit vehicle" src={editIcon} />
        </button>
      ),
    },
    {
      title: '',
      sortable: false,
      customCell: (row) => (
        <button
          type="button"
          className={styles.removeIcon}
          onClick={(e) => {
            e.preventDefault();
            deleteManufacturer(row);
          }}
          aria-label="remove traveler"
        >
          <img alt="remove traveler" src={removeIcon} />
        </button>
      ),
    },
  ];

  return (
    <div id={styles.vehiclesContainer}>
      <div className={cs(styles.vehiclesTitleRow, 'headerRow')}>
        <div className="header">{t('headerTitles.manufacturers')}</div>
        <div>
          <button
            type="button"
            className={styles.createIcon}
            onClick={(e) => {
              e.preventDefault();
              setOpenModal(true);
            }}
            aria-label="create vehicle"
          >
            <FontAwesomeIcon icon={faPlusCircle} color="#2957A4" size="2x" />
          </button>
          <button
            label="icon"
            type="button"
            className={cs({
              [styles.filterIcon]: true,
              [styles.expanded]: !isCollapsed,
              white_button: true,
            })}
            onClick={toggleFiltersBlock}
          />
        </div>
      </div>

      {!isCollapsed && (
        <CustomTableFilters
          filters={filters}
          setFilters={setFilters}
          columns={columns}
          itemsPerRow={2}
        />
      )}
      <DataTable
        ref={tableRef}
        data={manufacturers.data}
        totalCount={manufacturers.total}
        columns={columns}
        onParamsChanges={loadData}
        loading={isLoading}
      />
      <ManufacturerModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        modalHeaderText={modalHeaderText}
        manufacturer={manufacturer}
        setManufacturer={setManufacturer}
        loadData={() => tableRef.current.loadCurrentData()}
      />
    </div>
  );
};

export default ManufacturersTable;
